import { useMutation } from '@apollo/client/react';
import { CREATE_INVOICE_PDF_MUTATION } from '../../integrations/allkort/graphql/gql/InvoicePdfMutation';
import {
	CreateInvoicePdfResponse
} from '../../integrations/allkort/graphql/interface/InvoicePdf';

/**
 * useCreateInvoicePdf
 * 
 * Custom hook for generating a PDF for a card invoice.
 * Returns a URL that can be used to download the invoice.
 * 
 * @example
 * ```tsx
 * const { createInvoicePdf, loading, error, data } = useCreateInvoicePdf();
 * 
 * // Generate invoice PDF
 * const handleDownload = async (invoiceId: number) => {
 *   try {
 *     const result = await createInvoicePdf(invoiceId);
 *     if (result?.url) {
 *       window.open(result.url, '_blank');
 *     }
 *   } catch (err) {
 *     console.error('Failed to generate invoice:', err);
 *   }
 * };
 * ```
 */
export const useCreateInvoicePdf = () => {
	const [createInvoicePdfMutation, { loading, error, data }] = useMutation<
		CreateInvoicePdfResponse,
		{ id: number }
	>(CREATE_INVOICE_PDF_MUTATION);

	/**
	 * Generate a PDF for an invoice
	 * @param invoiceId - The invoice ID (integer)
	 * @returns The PDF data with URL and expiration date, or null if failed
	 */
	const createInvoicePdf = async (invoiceId: number) => {
		try {
			const result = await createInvoicePdfMutation({
				variables: { id: invoiceId }
			});

			// Log error details if mutation failed
			if (result.error) {
				console.error('Failed to create invoice PDF:', result.error.message);
				if ('graphQLErrors' in result.error && Array.isArray(result.error.graphQLErrors) && result.error.graphQLErrors.length > 0) {
					console.error('GraphQL errors:', result.error.graphQLErrors);
				}
				if ('networkError' in result.error && result.error.networkError) {
					console.error('Network error:', result.error.networkError);
				}
			}

			if (result.data?.createCardInvoicePdf?.pdfData) {
				return result.data.createCardInvoicePdf.pdfData;
			}
			return null;
		} catch (err) {
			console.error('Error creating invoice PDF:', err);
			throw err;
		}
	};

	return {
		createInvoicePdf,
		loading,
		error,
		data: data?.createCardInvoicePdf?.pdfData ?? null
	};
};
